<template>
    <div class="api-tools">
      <v-container fluid>
        <v-row>
          <!-- API-Endpunkt-Tester -->
          <v-col cols="12">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="me-2">mdi-api</v-icon>
                API-Endpunkt-Tester
              </v-card-title>
              <v-card-text>
                <v-form @submit.prevent="executeRequest">
                  <v-row>
                    <v-col cols="12" sm="2">
                      <v-select
                        v-model="request.method"
                        :items="['GET', 'POST', 'PUT', 'DELETE']"
                        label="Methode"
                        density="comfortable"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="10">
                      <v-text-field
                        v-model="request.url"
                        label="API-URL"
                        density="comfortable"
                        placeholder="z.B. /api/users.php"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-expansion-panels>
                    <v-expansion-panel title="Headers">
                      <v-expansion-panel-text>
                        <div v-for="(header, index) in request.headers" :key="index" class="d-flex mb-2">
                          <v-text-field
                            v-model="header.key"
                            label="Header-Name"
                            density="comfortable"
                            class="me-2"
                          ></v-text-field>
                          <v-text-field
                            v-model="header.value"
                            label="Header-Wert"
                            density="comfortable"
                          ></v-text-field>
                          <v-btn icon="mdi-delete" variant="text" @click="removeHeader(index)"></v-btn>
                        </div>
                        <v-btn
                          prepend-icon="mdi-plus"
                          variant="outlined"
                          @click="addHeader"
                          size="small"
                          class="mt-2"
                        >
                          Header hinzufügen
                        </v-btn>
                      </v-expansion-panel-text>
                    </v-expansion-panel>

                    <v-expansion-panel title="Body" v-if="request.method !== 'GET'">
                      <v-expansion-panel-text>
                        <v-textarea
                          v-model="request.body"
                          label="Request-Body (JSON)"
                          rows="10"
                          auto-grow
                          density="comfortable"
                          hint="Geben Sie hier JSON-Daten ein"
                        ></v-textarea>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <div class="d-flex justify-end mt-4">
                    <v-btn
                      color="primary"
                      type="submit"
                      :loading="loading"
                      prepend-icon="mdi-send"
                    >
                      Anfrage senden
                    </v-btn>
                  </div>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Antwort-Bereich -->
          <v-col cols="12" v-if="response.executed">
            <v-card :color="response.success ? 'success-container' : 'error-container'">
              <v-card-title class="d-flex justify-space-between">
                <div class="d-flex align-center">
                  <v-icon
                    class="me-2"
                    :color="response.success ? 'success' : 'error'"
                  >
                    {{ response.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  <span>API-Antwort</span>
                </div>
                <div>
                  <v-chip
                    class="ms-2"
                    :color="response.statusCode < 300 ? 'success' : (response.statusCode < 400 ? 'warning' : 'error')"
                    size="small"
                  >
                    Status: {{ response.statusCode }}
                  </v-chip>
                  <v-chip
                    class="ms-2"
                    color="primary"
                    size="small"
                  >
                    {{ response.time }}ms
                  </v-chip>
                </div>
              </v-card-title>
              <v-card-text>
                <v-tabs v-model="activeResponseTab">
                  <v-tab value="body">Body</v-tab>
                  <v-tab value="headers">Headers</v-tab>
                </v-tabs>

                <v-window v-model="activeResponseTab" class="mt-2">
                  <v-window-item value="body">
                    <pre class="response-content">{{ formatJson(response.data) }}</pre>
                  </v-window-item>
                  <v-window-item value="headers">
                    <table class="headers-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Wert</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in response.headers" :key="key">
                          <td>{{ key }}</td>
                          <td>{{ value }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </v-window-item>
                </v-window>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- WebSocket-Tester -->
          <v-col cols="12" class="mt-4">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="me-2">mdi-web-sync</v-icon>
                WebSocket-Tester
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="10">
                    <v-text-field
                      v-model="wsUrl"
                      label="WebSocket-URL"
                      density="comfortable"
                      :placeholder="defaultWsUrl"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="2" class="d-flex align-center">
                    <v-btn
                      v-if="!wsConnected"
                      color="primary"
                      @click="connectWebSocket"
                      :loading="wsConnecting"
                      block
                    >
                      Verbinden
                    </v-btn>
                    <v-btn
                      v-else
                      color="error"
                      @click="disconnectWebSocket"
                      block
                    >
                      Trennen
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-if="wsConnected">
                  <v-col cols="12">
                    <v-textarea
                      v-model="wsMessage"
                      label="Nachricht (JSON)"
                      rows="5"
                      auto-grow
                      density="comfortable"
                      placeholder='{"action": "status", "data": {"status": "online"}}'
                    ></v-textarea>

                    <div class="d-flex justify-end">
                      <v-btn
                        color="primary"
                        @click="sendWsMessage"
                        :disabled="!wsMessage"
                        prepend-icon="mdi-send"
                      >
                        Nachricht senden
                      </v-btn>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <h4 class="text-subtitle-1">Kommunikationsprotokoll</h4>
                      <v-btn icon="mdi-delete" variant="text" @click="clearWsLogs"></v-btn>
                    </div>
                    <v-sheet class="ws-log-container pa-2" bordered rounded>
                      <div v-for="(log, index) in wsLogs" :key="index" class="ws-log-entry" :class="`ws-${log.type}`">
                        <span class="ws-timestamp">{{ log.timestamp }}</span>
                        <span class="ws-direction" :class="`ws-${log.type}`">
                          {{ log.type === 'received' ? '← EMPFANGEN' : '→ GESENDET' }}
                        </span>
                        <pre class="ws-content">{{ log.content }}</pre>
                      </div>
                      <div v-if="wsLogs.length === 0" class="text-center text-body-2 text-disabled py-4">
                        Keine Nachrichten vorhanden
                      </div>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </template>

  <script setup>
   
  import { ref, computed, onBeforeUnmount } from 'vue';
  import axios from 'axios';
  import { API_BASE_URL, WS_URL } from '@/config';
  import { useToast } from 'vue-toastification';

  const toast = useToast();
  const loading = ref(false);
  const activeResponseTab = ref('body');

  // API Request
  const request = ref({
    method: 'GET',
    url: '',
    headers: [{ key: 'Content-Type', value: 'application/json' }],
    body: '{}'
  });

  // API Response
  const response = ref({
    executed: false,
    success: false,
    statusCode: 0,
    data: null,
    headers: {},
    time: 0
  });

  const addHeader = () => {
    request.value.headers.push({ key: '', value: '' });
  };

  const removeHeader = (index) => {
    request.value.headers.splice(index, 1);
  };

  const executeRequest = async () => {
    loading.value = true;
    response.value.executed = false;

    try {
      // Headers zusammenstellen
      const headers = {};
      request.value.headers.forEach(header => {
        if (header.key && header.value) {
          headers[header.key] = header.value;
        }
      });

      // URL vorbereiten
      let url = request.value.url;
      if (!url.startsWith('http') && !url.startsWith('/')) {
        url = `${API_BASE_URL}${url}`;
      } else if (url.startsWith('/api/')) {
        url = `${API_BASE_URL}${url.substring(5)}`;
      } else if (url.startsWith('/')) {
        url = `${API_BASE_URL}${url.substring(1)}`;
      }

      // Request ausführen
      const startTime = performance.now();

      const config = {
        method: request.value.method,
        url: url,
        headers: headers,
        withCredentials: true
      };

      if (request.value.method !== 'GET' && request.value.body) {
        try {
          config.data = JSON.parse(request.value.body);
        } catch (error) {
          toast.error('Ungültiges JSON im Request-Body');
          loading.value = false;
          return;
        }
      }

      const res = await axios(config);

      const endTime = performance.now();

      response.value = {
        executed: true,
        success: true,
        statusCode: res.status,
        data: res.data,
        headers: res.headers,
        time: Math.round(endTime - startTime)
      };
    } catch (error) {
      const endTime = performance.now();

      response.value = {
        executed: true,
        success: false,
        statusCode: error.response?.status || 0,
        data: error.response?.data || error.message,
        headers: error.response?.headers || {},
        time: Math.round(endTime - startTime)
      };
    } finally {
      loading.value = false;
    }
  };

  const formatJson = (json) => {
    try {
      if (typeof json === 'string') {
        return JSON.stringify(JSON.parse(json), null, 2);
      } else {
        return JSON.stringify(json, null, 2);
      }
    } catch (e) {
      return json;
    }
  };

  // WebSocket-Tester
  const defaultWsUrl = computed(() => WS_URL);
  const wsUrl = ref('');
  const wsConnected = ref(false);
  const wsConnecting = ref(false);
  const wsSocket = ref(null);
  const wsMessage = ref('');
  const wsLogs = ref([]);

  const connectWebSocket = () => {
    try {
      wsConnecting.value = true;
      const url = wsUrl.value || defaultWsUrl.value;
      wsSocket.value = new WebSocket(url);

      wsSocket.value.onopen = () => {
        wsConnected.value = true;
        wsConnecting.value = false;
        toast.success('WebSocket-Verbindung hergestellt');
        addWsLog('connected', 'Verbindung hergestellt');
      };

      wsSocket.value.onmessage = (event) => {
        addWsLog('received', event.data);
      };

      wsSocket.value.onerror = (error) => {
        toast.error('WebSocket-Fehler aufgetreten');
        console.error('WebSocket error:', error);
        wsConnecting.value = false;
      };

      wsSocket.value.onclose = () => {
        wsConnected.value = false;
        wsConnecting.value = false;
        addWsLog('disconnected', 'Verbindung geschlossen');
      };
    } catch (error) {
      toast.error(`WebSocket-Verbindungsfehler: ${error.message}`);
      wsConnecting.value = false;
    }
  };

  const disconnectWebSocket = () => {
    if (wsSocket.value && wsSocket.value.readyState === WebSocket.OPEN) {
      wsSocket.value.close();
    }
    wsConnected.value = false;
  };

  const sendWsMessage = () => {
    if (wsSocket.value && wsSocket.value.readyState === WebSocket.OPEN) {
      wsSocket.value.send(wsMessage.value);
      addWsLog('sent', wsMessage.value);
    } else {
      toast.error('WebSocket nicht verbunden');
    }
  };

  const addWsLog = (type, content) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString();
    wsLogs.value.unshift({
      timestamp,
      type,
      content
    });
  };

  const clearWsLogs = () => {
    wsLogs.value = [];
  };

  // Verbindung bei Komponentenabbau schließen
  onBeforeUnmount(() => {
    if (wsSocket.value) {
      wsSocket.value.close();
    }
  });
   
  </script>

  <style scoped>
  .api-tools {
    padding: 16px;
  }

  .response-container {
    max-height: 400px;
    overflow-y: auto;
    background-color: #f5f5f5;
    border-radius: 4px;
  }

  .response-pre {
    white-space: pre-wrap;
    word-break: break-word;
  }
  </style>
